import type { Cc7Logger } from "@rinami/cc7-ng";
import { Logger } from "./logger.service";

/**
 * Bridges the CC7 connector library's own logger (`Cc7Logger` — only `debug`/`warn`, no
 * `info`/`error`) into `Logger`, tagged `"cc7"`, so CC7 diagnostics are captured and persisted
 * like every other log entry — replacing the previous `CC7_CONSOLE_LOGGER`/`CC7_SILENT_LOGGER`
 * switch, which discarded everything outside debug builds.
 *
 * CC7 diagnostics go through the same `logLevel` filter as app logs, with no bypass: with the
 * production default `logLevel: "INFO"`, CC7 `warn` is persisted to `gciins-logs-db` and CC7
 * `debug` is dropped; `logLevel: "DEBUG"` captures both. Real console mirroring stays gated by
 * `logLevel === "DEBUG"`, via `Logger`'s own mirror step — this bridge never touches `console.*`
 * or IndexedDB directly itself.
 *
 * CC7's `context` is typed `unknown` (it may be an `Error`, an HTTP response, a circular
 * structure...) and is passed through as-is — `Logger` sanitizes every entry's `context` itself.
 *
 * @see Docs/app-logs.md#cc7-logger-bridge
 */
export function createCc7LoggerBridge(logger: Logger): Cc7Logger {
  return {
    debug: (message, context) => logger.debug("cc7", message, context),
    warn: (message, context) => logger.warn("cc7", message, context),
  };
}
